// The arithmetic behind the Choir summary. Pure functions over the three
// record shapes, so the screen only lays the answers out.

import { countBy, lastMonths, upcoming, recent, amountOf, dateOf, isoOf } from '../../utils/summaryStats';
import type { NamedCount, MonthBucket } from '../../utils/summaryStats';

export interface ChoirMemberLike {
  name?: string;
  voiceType?: string;
  role?: string;
  /** Active / Inactive / On Leave… — the lifecycle `status` is ACTIVE/VOID. */
  memberStatus?: string;
  joinedDate?: string;
}

export interface ChoirScheduleLike {
  date?: string;
  time?: string;
  eventType?: string;
  conductor?: string;
  headcount?: unknown;
  songs?: string;
}

export interface ChoirAssetLike {
  title?: string;
  category?: string;
  /** Available / Issued / Maintenance / Lost/Damaged. */
  assetStatus?: string;
  assignedTo?: string;
}

/** A member counts as singing unless marked otherwise; a blank status is Active. */
export const isSinging = (m: ChoirMemberLike): boolean =>
  String(m.memberStatus || 'Active').trim().toLowerCase() === 'active';

export interface ChoirSummary {
  singing: number;
  away: ChoirMemberLike[];
  voices: NamedCount[];
  roles: NamedCount[];
  newThisYear: number;
  upcoming: ChoirScheduleLike[];
  practicesLast30: number;
  servicesLast30: number;
  /** Average headcount over dated schedules of the last 90 days that recorded one. */
  avgHeadcount: number | null;
  months: MonthBucket[];
  assetsByStatus: NamedCount[];
  assetsByCategory: NamedCount[];
  issued: ChoirAssetLike[];
  needsAttention: ChoirAssetLike[];
}

const isPractice = (s: ChoirScheduleLike) => /practi[cs]e|rehears/i.test(String(s.eventType || ''));

export const choirSummary = (
  members: ChoirMemberLike[], schedules: ChoirScheduleLike[], assets: ChoirAssetLike[], now: Date = new Date()
): ChoirSummary => {
  const singing = members.filter(isSinging);
  const last30 = recent(schedules, (s) => s.date, 30, now);
  const withCount = recent(schedules, (s) => s.date, 90, now).filter((s) => amountOf(s.headcount) > 0);
  const year = String(now.getFullYear());
  const statusOf = (a: ChoirAssetLike) => a.assetStatus || 'Available';

  return {
    singing: singing.length,
    away: members.filter((m) => !isSinging(m)),
    voices: countBy(singing, (m) => m.voiceType),
    roles: countBy(singing, (m) => m.role),
    newThisYear: members.filter((m) => (dateOf(m.joinedDate) || '').startsWith(year)).length,
    upcoming: upcoming(schedules, (s) => s.date, 30, now),
    practicesLast30: last30.filter(isPractice).length,
    servicesLast30: last30.filter((s) => !isPractice(s)).length,
    avgHeadcount: withCount.length
      ? Math.round(withCount.reduce((n, s) => n + amountOf(s.headcount), 0) / withCount.length)
      : null,
    months: lastMonths(schedules.filter((s) => (dateOf(s.date) || '') <= isoOf(now)), (s) => s.date, 6, now),
    assetsByStatus: countBy(assets, statusOf),
    assetsByCategory: countBy(assets, (a) => a.category),
    issued: assets.filter((a) => statusOf(a) === 'Issued'),
    needsAttention: assets.filter((a) => ['Maintenance', 'Lost/Damaged'].includes(statusOf(a)))
  };
};
